import { hoursLabel } from '../calendar/calendar-layout'
import {
  AGE_SCHEDULING_MAX_AGE,
  type AgeSchedulingPolicy,
} from './settings-contracts'

/**
 * Age-based scheduling as age groups. When the stored ranges already cover
 * every age (the only shape that can be turned on), the Dashboard keeps them
 * that way: a group is added by splitting the group that holds its first age,
 * moving a group's first age moves the previous group's last age, and a
 * deleted group's ages join its neighbour. Gaps and overlaps can't be made.
 */

export type AgeWindowDraft = { key: string; start: string; end: string }
export type AgeGroupDraft = {
  key: string
  minimumYears: number
  /** Omitted: open-ended ("13 and older"). A stored 130 is kept as 130. */
  maximumYears?: number
  windows: AgeWindowDraft[]
}

type DayRule =
  { status: 'CLOSED' } | { status: 'OPEN'; start: string; end: string }

export function minuteOf(value: string) {
  const [hour = 0, minute = 0] = value.split(':').map(Number)
  return hour * 60 + minute
}

function lastAge(group: { maximumYears?: number }) {
  return group.maximumYears ?? AGE_SCHEDULING_MAX_AGE
}

function openEnded(group: AgeGroupDraft, minimumYears = group.minimumYears) {
  return { key: group.key, minimumYears, windows: group.windows }
}

export function sortedGroups(groups: AgeGroupDraft[]) {
  return [...groups].sort(
    (left, right) =>
      left.minimumYears - right.minimumYears || lastAge(left) - lastAge(right)
  )
}

/** Every age from 0 belongs to exactly one group. */
export function coversEveryAge(groups: AgeGroupDraft[]) {
  const sorted = sortedGroups(groups)
  const last = sorted[sorted.length - 1]
  return (
    last !== undefined &&
    sorted[0]?.minimumYears === 0 &&
    lastAge(last) === AGE_SCHEDULING_MAX_AGE &&
    sorted.slice(1).every((group, index) => {
      const previous = sorted[index]?.maximumYears
      return previous !== undefined && previous + 1 === group.minimumYears
    })
  )
}

/** The ages no group holds, as labels ("Age 6", "Ages 13 and older"). */
export function missingAges(groups: AgeGroupDraft[]) {
  const missing: string[] = []
  let next = 0
  for (const group of sortedGroups(groups)) {
    if (group.minimumYears > next) {
      missing.push(
        ageGroupLabel({
          minimumYears: next,
          maximumYears: group.minimumYears - 1,
        })
      )
    }
    next = Math.max(next, lastAge(group) + 1)
  }
  if (groups.length && next <= AGE_SCHEDULING_MAX_AGE) {
    missing.push(ageGroupLabel({ minimumYears: next }))
  }
  return missing
}

/** "Ages 0–5" in the middle of a sentence. */
export function lowerFirst(label: string) {
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/** "Age 6 and ages 13 and older have no group", or null when every age has one. */
export function missingAgesSummary(groups: AgeGroupDraft[]) {
  const labels = missingAges(groups).map((label, index) =>
    index ? lowerFirst(label) : label
  )
  const last = labels[labels.length - 1]
  if (!last) return null
  const list =
    labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${last}` : last
  return `${list} ${labels.length > 1 ? 'have' : 'has'} no group`
}

/** Groups can be edited by their first age: empty, or already covering. */
export function editsByFirstAge(groups: AgeGroupDraft[]) {
  return groups.length === 0 || coversEveryAge(groups)
}

export function ageGroupLabel(
  group: { minimumYears: number; maximumYears?: number },
  { alone = false }: { alone?: boolean } = {}
) {
  const max = lastAge(group)
  if (group.minimumYears === 0 && max === AGE_SCHEDULING_MAX_AGE && alone) {
    return 'All ages'
  }
  if (max === AGE_SCHEDULING_MAX_AGE) {
    return `Ages ${group.minimumYears} and older`
  }
  return group.minimumYears === max
    ? `Age ${max}`
    : `Ages ${group.minimumYears}–${max}`
}

export function windowsLabel(windows: Array<{ start: string; end: string }>) {
  const valid = windows
    .map(({ start, end }) => ({
      startMinute: minuteOf(start),
      endMinute: minuteOf(end),
    }))
    .filter(
      ({ startMinute, endMinute }) =>
        Number.isFinite(startMinute) &&
        Number.isFinite(endMinute) &&
        endMinute > startMinute
    )
    .sort((left, right) => left.startMinute - right.startMinute)
  return valid.length ? hoursLabel(valid) : 'No bookable times'
}

/** The first ages a group may start at: after the previous group's first age, up to its own last age. */
export function firstAgeBounds(groups: AgeGroupDraft[], key: string) {
  const sorted = sortedGroups(groups)
  const index = sorted.findIndex((group) => group.key === key)
  const group = sorted[index]
  const previous = sorted[index - 1]
  if (!group || !previous) return null
  return { min: previous.minimumYears + 1, max: lastAge(group) }
}

/** The group a new group starting at `age` would split, if any. */
export function groupHolding(groups: AgeGroupDraft[], age: number) {
  return sortedGroups(groups).find(
    (group) => group.minimumYears <= age && age <= lastAge(group)
  )
}

export function newGroupAgeError(groups: AgeGroupDraft[], age: number) {
  if (!Number.isInteger(age) || age < 1 || age > AGE_SCHEDULING_MAX_AGE) {
    return `Enter a whole age from 1 to ${AGE_SCHEDULING_MAX_AGE}.`
  }
  const holder = groupHolding(groups, age)
  if (holder?.minimumYears === age) {
    return `A group already starts at age ${age}.`
  }
  return null
}

/** Splits the group holding `age`: it keeps the ages before, the new group takes the rest. */
export function addGroupAt(
  groups: AgeGroupDraft[],
  age: number,
  group: Pick<AgeGroupDraft, 'key' | 'windows'>
): AgeGroupDraft[] {
  const holder = groupHolding(groups, age)
  if (!holder || holder.minimumYears === age) return groups
  const added: AgeGroupDraft = {
    key: group.key,
    minimumYears: age,
    ...(holder.maximumYears === undefined
      ? {}
      : { maximumYears: holder.maximumYears }),
    windows: group.windows,
  }
  return groups.flatMap((candidate) =>
    candidate.key === holder.key
      ? [{ ...candidate, maximumYears: age - 1 }, added]
      : [candidate]
  )
}

/** Moves a group's first age; the previous group's last age follows it. */
export function moveFirstAge(
  groups: AgeGroupDraft[],
  key: string,
  age: number
): AgeGroupDraft[] {
  const sorted = sortedGroups(groups)
  const index = sorted.findIndex((group) => group.key === key)
  const previous = sorted[index - 1]
  const bounds = firstAgeBounds(groups, key)
  if (!previous || !bounds || age < bounds.min || age > bounds.max) {
    return groups
  }
  return groups.map((group) =>
    group.key === key
      ? { ...group, minimumYears: age }
      : group.key === previous.key
        ? { ...group, maximumYears: age - 1 }
        : group
  )
}

/** The group a deleted group's ages join: the one before it, else the one after. */
export function absorbingGroup(groups: AgeGroupDraft[], key: string) {
  const sorted = sortedGroups(groups)
  const index = sorted.findIndex((group) => group.key === key)
  return index < 0 ? undefined : (sorted[index - 1] ?? sorted[index + 1])
}

export function deleteGroup(groups: AgeGroupDraft[], key: string) {
  const removed = groups.find((group) => group.key === key)
  const rest = groups.filter((group) => group.key !== key)
  if (!removed || !coversEveryAge(groups)) return rest
  const absorber = absorbingGroup(groups, key)
  if (!absorber) return rest
  return rest.map((group) => {
    if (group.key !== absorber.key) return group
    if (absorber.minimumYears < removed.minimumYears) {
      // The group before takes the removed ages, up to its last one.
      return removed.maximumYears === undefined
        ? openEnded(group)
        : { ...group, maximumYears: removed.maximumYears }
    }
    return { ...group, minimumYears: removed.minimumYears }
  })
}

/**
 * Closes gaps without taking ages from any group: the first group starts at
 * 0, each group runs to the next one's first age, the last is open-ended.
 * Only reachable for stored ranges that don't already cover every age.
 */
export function extendToEveryAge(groups: AgeGroupDraft[]): AgeGroupDraft[] {
  const sorted = sortedGroups(groups)
  const next = new Map(
    sorted.map((group, index) => [group.key, sorted[index + 1]])
  )
  return groups.map((group) => {
    const following = next.get(group.key)
    const first = sorted[0]?.key === group.key ? 0 : group.minimumYears
    if (!following) return openEnded(group, first)
    return {
      ...group,
      minimumYears: first,
      maximumYears: following.minimumYears - 1,
    }
  })
}

/** The policy in one field order, so equal policies compare equal. */
export function canonicalAgePolicy(policy: {
  enabled: boolean
  rules: Array<{
    minimumYears: number
    maximumYears?: number
    windows: Array<{ start: string; end: string }>
  }>
}): AgeSchedulingPolicy {
  return {
    enabled: policy.enabled,
    rules: policy.rules.map((rule) => ({
      minimumYears: rule.minimumYears,
      ...(rule.maximumYears === undefined
        ? {}
        : { maximumYears: rule.maximumYears }),
      windows: rule.windows.map(({ start, end }) => ({ start, end })),
    })),
  }
}

export function payloadFromGroups(
  enabled: boolean,
  groups: AgeGroupDraft[]
): AgeSchedulingPolicy {
  return canonicalAgePolicy({ enabled, rules: groups })
}

function clock(minutes: number) {
  const bounded = Math.min(Math.max(minutes, 0), 23 * 60 + 59)
  return `${String(Math.floor(bounded / 60)).padStart(2, '0')}:${String(bounded % 60).padStart(2, '0')}`
}

/** A new time: up to two hours of the first stretch of clinic hours not yet bookable. */
export function suggestedWindow(
  windows: Array<{ start: string; end: string }>,
  span: { start: string; end: string } | null
) {
  const day = span ?? { start: '08:00', end: '17:00' }
  const taken = windows
    .filter((window) => window.start && window.end && window.end > window.start)
    .map((window) => ({
      start: minuteOf(window.start),
      end: minuteOf(window.end),
    }))
    .sort((left, right) => left.start - right.start)
  const dayEnd = minuteOf(day.end)
  let cursor = minuteOf(day.start)
  let gapEnd = dayEnd
  for (const window of taken) {
    if (window.start > cursor) {
      gapEnd = Math.min(window.start, dayEnd)
      break
    }
    cursor = Math.max(cursor, window.end)
  }
  if (cursor < gapEnd) {
    return { start: clock(cursor), end: clock(Math.min(gapEnd, cursor + 120)) }
  }
  // The clinic's day is already bookable: an hour after the last time.
  const after = Math.max(...taken.map((window) => window.end), cursor)
  return {
    start: clock(Math.min(after, 23 * 60)),
    end: clock(Math.min(after, 23 * 60) + 60),
  }
}

/** Per time row: what's wrong with it, if anything. */
export function windowIssues(windows: Array<{ start: string; end: string }>) {
  const issues = windows.map((window): string | null =>
    !window.start || !window.end
      ? 'Enter a start and end time.'
      : window.end <= window.start
        ? 'End time must be later than start time.'
        : null
  )
  const order = windows
    .map((window, index) => ({ ...window, index }))
    .filter((_, index) => issues[index] === null)
    .sort((left, right) => left.start.localeCompare(right.start))
  order.slice(1).forEach((window, position) => {
    if (order[position]!.end > window.start) {
      issues[window.index] = 'Times must not overlap.'
    }
  })
  return issues
}

/** The clinic's earliest opening and latest closing across the week. */
export function clinicDaySpan(
  workingHours: Record<string, DayRule> | undefined
) {
  const open = Object.values(workingHours ?? {}).filter(
    (day): day is Extract<DayRule, { status: 'OPEN' }> =>
      day.status === 'OPEN' && day.end > day.start
  )
  if (!open.length) return null
  return {
    start: open.reduce(
      (min, day) => (day.start < min ? day.start : min),
      '23:59'
    ),
    end: open.reduce((max, day) => (day.end > max ? day.end : max), '00:00'),
  }
}

export type TimelineRange = {
  startMinute: number
  endMinute: number
  ticks: number[]
}

/** Whole hours around the clinic's day and every shown time, at least four hours wide. */
export function timelineRange(
  span: { start: string; end: string } | null,
  windows: Array<{ start: string; end: string }>
): TimelineRange {
  const minutes = [
    ...(span ? [minuteOf(span.start), minuteOf(span.end)] : [8 * 60, 17 * 60]),
    ...windows
      .filter(
        (window) => window.start && window.end && window.end > window.start
      )
      .flatMap((window) => [minuteOf(window.start), minuteOf(window.end)]),
  ]
  const earliest = Math.min(...minutes)
  const latest = Math.max(...minutes)
  // Labels every two hours on a long day, on even hours so both ends get one.
  const step = Math.ceil(latest / 60) - Math.floor(earliest / 60) > 8 ? 120 : 60
  let startMinute = Math.floor(earliest / step) * step
  let endMinute = Math.ceil(latest / step) * step
  if (endMinute - startMinute < 240) {
    endMinute = Math.min(24 * 60, startMinute + 240)
    startMinute = Math.min(startMinute, endMinute - 240)
  }
  const ticks: number[] = []
  for (let minute = startMinute; minute <= endMinute; minute += step) {
    ticks.push(minute)
  }
  return { startMinute, endMinute, ticks }
}

/** Whether part of the shown day is outside the clinic's hours. */
export function showsClosedHours(
  range: TimelineRange,
  span: { start: string; end: string } | null
) {
  return (
    span !== null &&
    (range.startMinute < minuteOf(span.start) ||
      range.endMinute > minuteOf(span.end))
  )
}
